import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import SortingOptions, { SortingDirection } from 'common/types/SortingOptions';

import { JobStatus } from 'domain/entities/JobStatus';
import { ServiceStatus } from 'domain/entities/ServiceStatus';
import { Filters } from 'targets/web/modules/dashboard/components/FiltersContainer/FiltersContainer';

export type ViewType = 'grid-view' | 'table-view';
export type LogType = 'job-log' | 'service-log';

const viewTypeAtom = atomWithStorage<ViewType>('ViewType', 'grid-view');
const logTypeAtom = atomWithStorage<LogType>('LogType', 'job-log');
const filtersAtom = atom<Partial<Filters & { limit: number }>>({
  dateStart: undefined,
  dateEnd: undefined,
});

export type StatusTab =
  | 'all'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'not_ready'
  | 'canceled';

const statusTabAtom = atom<StatusTab>('all');

// "Not Ready" is a billing status, not a job status — a job carries both. Every job
// is created non_billable, so billing status alone matches ~97% of jobs; scoping to
// completed makes this the "work is done, waiting on billing review" queue.
// Mirrors NOT_READY_JOB_STATUSES / NOT_READY_SERVICE_STATUSES in the API — widening
// the tab means changing these lists and their API counterparts together.
const NOT_READY_JOB_STATUSES: JobStatus[] = ['completed'];
const NOT_READY_SERVICE_STATUSES: ServiceStatus[] = ['completed'];

const getJobStatus = (status: StatusTab): JobStatus[] => {
  switch (status) {
    case 'all':
      return ['scheduled', 'completed', 'in_progress'];
    case 'not_ready':
      return NOT_READY_JOB_STATUSES;
    default:
      return [status];
  }
};

const getServiceStatus = (status: StatusTab): ServiceStatus[] => {
  switch (status) {
    case 'all':
      return ['pending', 'in_progress', 'completed'];
    case 'scheduled':
      return ['pending'];
    case 'not_ready':
      return NOT_READY_SERVICE_STATUSES;
    default:
      return [status];
  }
};

// Only the Not Ready tab narrows by billing status; every other tab leaves it
// undefined so their results and counts are unchanged.
const getBillingStatus = (status: StatusTab): 'non_billable' | undefined =>
  status === 'not_ready' ? 'non_billable' : undefined;

const servicesTableSortAtom = atom<SortingOptions>({
  sortBy: 'status',
  direction: SortingDirection.Ascending,
});

const jobsTableSortAtom = atom<SortingOptions>({
  sortBy: 'status',
  direction: SortingDirection.Ascending,
});

function getCommonFilterFields(filters: Partial<Filters & { limit: number }>) {
  return {
    phrase: filters.phrase,
    customerId: filters.customerId,
    aircraftId: filters.aircraftId,
    serviceTypeId: filters.serviceTypeId,
    dateStart: filters.dateStart,
    dateEnd: filters.dateEnd,
    stationIds: filters.stations?.map((s) => s.id),
    airportIds: filters.airports?.map((a) => a.id),
    facilityIds: filters.facilities?.map((f) => f.id),
    limit: filters.limit,
  };
}

export const useJobViewFilters = () => {
  const [filters, setFilters] = useAtom(filtersAtom);
  const [viewType, setViewType] = useAtom(viewTypeAtom);
  const [logType, setLogType] = useAtom(logTypeAtom);
  const [statusTab, setStatusTab] = useAtom(statusTabAtom);
  const [servicesTableSort, setServicesTableSort] = useAtom(servicesTableSortAtom);
  const [jobsTableSort, setJobsTableSort] = useAtom(jobsTableSortAtom);

  const jobFilters = useMemo(
    () => ({
      ...getCommonFilterFields(filters),
      ...jobsTableSort,
      status: getJobStatus(statusTab),
      billingStatus: getBillingStatus(statusTab),
    }),
    [filters, statusTab, jobsTableSort],
  );
  const serviceFilters = useMemo(
    () => ({
      ...getCommonFilterFields(filters),
      ...servicesTableSort,
      status: getServiceStatus(statusTab),
      billingStatus: getBillingStatus(statusTab),
    }),
    [statusTab, filters, servicesTableSort],
  );
  const setSort = useCallback(
    (params: SortingOptions) =>
      logType === 'job-log' ? setJobsTableSort(params) : setServicesTableSort(params),
    [setServicesTableSort, setJobsTableSort, logType],
  );

  return {
    setSort,
    filters,
    setFilters,
    jobFilters,
    serviceFilters,
    statusTab,
    setStatusTab,
    viewType,
    setViewType,
    logType,
    setLogType,
  };
};
